import axios from 'axios';
import { auth, functions } from '../firebase/config';
import { Lane, getLanes, Receipt, saveReceipt } from './dbService';
import { generateReceiptFromEmail } from './receiptService';
import { httpsCallable } from 'firebase/functions';
import { PDFDocument, PDFPage } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Email search criteria
export interface EmailSearchCriteria {
  lanes: Lane[];
  afterDate?: Date;
}

interface PdfExtractedData {
  bolNumber: string | null;
  shipmentNumber: string | null;
  deliveryDate: Date | null;
  origin: string | null;
  destination: string | null;
}

// Email attachment
export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string;
  data?: string; // Base64 encoded data
}

// Email message
export interface EmailMessage {
  id: string;
  from: string;
  to: string;
  subject: string;
  date: Date;
  body: string;
  hasAttachment: boolean;
  attachments: EmailAttachment[];
}

// Get Gmail API token
const getGmailToken = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No user is signed in');
    }
    
    const token = await currentUser.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting Gmail token: ', error);
    throw error;
  }
};

// Search emails based on criteria
export const searchEmails = async (criteria: EmailSearchCriteria): Promise<EmailMessage[]> => {
  try {
    // This would typically be implemented using Firebase Cloud Functions
    // that would handle the Gmail API calls server-side for security reasons
    // For now, we're returning a mock implementation
    
    console.log('Searching emails with criteria:', criteria);
    
    // Mock implementation - in real app, this would call a Firebase function
    return [];
  } catch (error) {
    console.error('Error searching emails: ', error);
    throw error;
  }
};

// Helper function to extract text from PDF bytes using PDF.js
const extractTextFromPdf = async (pdfBytes: Uint8Array): Promise<string> => {
  try {
    // Load PDF document using PDF.js
    const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
    const pdf = await loadingTask.promise;
    
    console.log(`PDF loaded with ${pdf.numPages} pages`);
    
    let fullText = '';
    
    // Extract text from each page
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      // Concatenate the text items with proper spacing
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += `\n--- Page ${i} ---\n${pageText}\n`;
    }
    
    console.log(`Extracted ${fullText.length} characters of text from PDF`);
    return fullText;
  } catch (error) {
    console.error('Error extracting text from PDF using PDF.js:', error);
    
    // Fallback to basic extraction if PDF.js fails
    try {
      // Load PDF document using pdf-lib (as fallback)
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const numPages = pdfDoc.getPageCount();
      
      console.log(`Falling back to basic extraction for PDF with ${numPages} pages`);
      
      // Convert the PDF bytes to a string and look for text patterns
      const textDecoder = new TextDecoder('utf-8');
      let text = textDecoder.decode(pdfBytes);
      
      // Clean up the text - remove non-printable characters
      text = text.replace(/[^\x20-\x7E\r\n]/g, ' ');
      
      return text;
    } catch (fallbackError) {
      console.error('Fallback extraction also failed:', fallbackError);
      return '';
    }
  }
};

// Extract BOL number and other information from PDF
export const extractPdfData = async (pdfBytes: Uint8Array): Promise<PdfExtractedData> => {
  try {
    const result: PdfExtractedData = {
      bolNumber: null,
      shipmentNumber: null,
      deliveryDate: null,
      origin: null,
      destination: null
    };
    
    // Extract text from PDF
    const pageText = await extractTextFromPdf(pdfBytes);
    console.log('Extracted PDF text length:', pageText.length);
    
    // Log a small preview of the text for debugging
    console.log('Text preview:', pageText.substring(0, 500) + '...');
    
    // Extract BOL number - try different approaches
    if (!result.bolNumber) {
      // Pattern 1: Look for BOL/BL + number (common format)
      const bolPatterns = [
        /BOL\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /BOL\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Bill\s*of\s*Lading\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Bill\s*of\s*Lading\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /B\/L\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /BL\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /BOL\s*number\s*is\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /BOL\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i
      ];
      
      for (const pattern of bolPatterns) {
        const bolMatch = pageText.match(pattern);
        if (bolMatch && bolMatch[1]) {
          result.bolNumber = bolMatch[1].trim();
          console.log('Found BOL number (pattern match):', result.bolNumber);
          break;
        }
      }
      
      // Pattern 2: If still not found, look for patterns in context
      if (!result.bolNumber) {
        // Look for lines containing BOL related terms
        const bolLines = pageText.split(/\n/).filter(line => 
          /BOL|Bill of Lading|B\/L|BL/i.test(line)
        );
        
        console.log('Found BOL-related lines:', bolLines);
        
        // Extract numeric patterns from these lines
        for (const line of bolLines) {
          // Look for numberic patterns in the BOL lines
          const numericMatch = line.match(/[A-Z0-9][-A-Z0-9]*\d{6,10}/);
          if (numericMatch) {
            result.bolNumber = numericMatch[0].trim();
            console.log('Found BOL number (context match):', result.bolNumber);
            break;
          }
        }
      }
      
      // Pattern 3: Look for standalone number patterns that might be BOL numbers
      if (!result.bolNumber) {
        const standaloneMatch = pageText.match(/\b(\d{6,10})\b/);
        if (standaloneMatch) {
          result.bolNumber = standaloneMatch[1].trim();
          console.log('Found potential BOL number (numeric pattern):', result.bolNumber);
        }
      }
    }
    
    // Extract shipment number - try different approaches
    if (!result.shipmentNumber) {
      // Approach 1: Look for explicit shipment number patterns
      const shipmentPatterns = [
        /Shipment\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Shipment\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Load\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Load\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Reference\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Reference\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Order\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Order\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Trip\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Trip\s*Number\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /Pro\s*#?\s*[:\s]*([A-Z0-9][-A-Z0-9]*\d+)/i,
        /SCAC\s*[:\s]*([A-Z]{2,4}\d+)/i
      ];
      
      for (const pattern of shipmentPatterns) {
        const shipmentMatch = pageText.match(pattern);
        if (shipmentMatch && shipmentMatch[1]) {
          result.shipmentNumber = shipmentMatch[1].trim();
          console.log('Found shipment number (pattern match):', result.shipmentNumber);
          break;
        }
      }
      
      // Approach 2: Look for patterns in context if not found
      if (!result.shipmentNumber) {
        // Look for lines containing shipment related terms
        const shipmentLines = pageText.split(/\n/).filter(line => 
          /Shipment|Load|Reference|Order|Trip|Pro|SCAC/i.test(line)
        );
        
        console.log('Found shipment-related lines:', shipmentLines);
        
        // Extract numeric patterns from these lines
        for (const line of shipmentLines) {
          // Look for patterns with letters followed by numbers (common in shipment numbers)
          const alphaNumMatch = line.match(/[A-Z]{2,4}\d{6,10}/i);
          if (alphaNumMatch) {
            result.shipmentNumber = alphaNumMatch[0].trim();
            console.log('Found shipment number (context match):', result.shipmentNumber);
            break;
          }
          
          // Alternatively, look for just numbers
          if (!result.shipmentNumber) {
            const numericMatch = line.match(/\b(\d{6,10})\b/);
            if (numericMatch && (!result.bolNumber || result.bolNumber !== numericMatch[1])) {
              result.shipmentNumber = numericMatch[1].trim();
              console.log('Found shipment number (numeric match):', result.shipmentNumber);
              break;
            }
          }
        }
      }
    }
    
    // Extract delivery date - look for date patterns near keywords
    if (!result.deliveryDate) {
      // First, find lines containing date-related keywords
      const dateLines = pageText.split(/\n/).filter(line => 
        /Delivery\s*Date|Del\s*Date|Appointment|Schedule|Due|Delivery|Arrive/i.test(line)
      );
      
      console.log('Found date-related lines:', dateLines);
      
      // Look for date patterns in these lines
      for (const line of dateLines) {
        // Match common US date formats (MM/DD/YYYY, MM-DD-YYYY, etc.)
        const dateMatch = line.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
        if (dateMatch) {
          const dateStr = dateMatch[0].trim();
          const parsedDate = parseDate(dateStr);
          if (parsedDate) {
            result.deliveryDate = parsedDate;
            console.log('Found delivery date (line match):', result.deliveryDate.toISOString());
            break;
          }
        }
      }
      
      // If no date found in specific lines, try to find any date in the document
      if (!result.deliveryDate) {
        const anyDateMatch = pageText.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
        if (anyDateMatch) {
          const dateStr = anyDateMatch[0].trim();
          const parsedDate = parseDate(dateStr);
          if (parsedDate) {
            result.deliveryDate = parsedDate;
            console.log('Found potential delivery date (any date):', result.deliveryDate.toISOString());
          }
        }
      }
    }
    
    // Extract destination and origin
    // For these address fields, we'll look for address patterns near specific keywords
    
    // Look for destination address patterns
    if (!result.destination) {
      // Find lines containing destination-related keywords
      const destLines = pageText.split(/\n/).filter(line => 
        /Destination|Deliver\s*To|Consignee|Ship\s*To|Delivery\s*Address/i.test(line)
      );
      
      console.log('Found destination-related lines:', destLines);
      
      // Try to extract addresses from these lines and surrounding context
      for (let i = 0; i < destLines.length; i++) {
        const line = destLines[i];
        
        // Check for city/state pattern in this line
        const addressMatch = line.match(/([A-Za-z\s\.]+,\s*[A-Z]{2}(?:\s*\d{5})?)/);
        if (addressMatch) {
          result.destination = addressMatch[1].trim();
          console.log('Found destination (line match):', result.destination);
          break;
        }
        
        // If we have at least 3 more lines after this keyword line, check them for address patterns
        const lineIndex = pageText.indexOf(line);
        if (lineIndex >= 0) {
          // Get a chunk of text after this line to look for address patterns
          const followingText = pageText.substring(lineIndex, lineIndex + 200);
          const followingMatch = followingText.match(/([A-Za-z\s\.]+,\s*[A-Z]{2}(?:\s*\d{5})?)/);
          if (followingMatch) {
            result.destination = followingMatch[1].trim();
            console.log('Found destination (context match):', result.destination);
            break;
          }
        }
      }
    }
    
    // Look for origin address patterns (similar to destination)
    if (!result.origin) {
      // Find lines containing origin-related keywords
      const originLines = pageText.split(/\n/).filter(line => 
        /Origin|Shipper|Pickup|Ship\s*From|Pickup\s*Address/i.test(line)
      );
      
      console.log('Found origin-related lines:', originLines);
      
      // Try to extract addresses from these lines and surrounding context
      for (let i = 0; i < originLines.length; i++) {
        const line = originLines[i];
        
        // Check for city/state pattern in this line
        const addressMatch = line.match(/([A-Za-z\s\.]+,\s*[A-Z]{2}(?:\s*\d{5})?)/);
        if (addressMatch) {
          result.origin = addressMatch[1].trim();
          console.log('Found origin (line match):', result.origin);
          break;
        }
        
        // If we have at least 3 more lines after this keyword line, check them for address patterns
        const lineIndex = pageText.indexOf(line);
        if (lineIndex >= 0) {
          // Get a chunk of text after this line to look for address patterns
          const followingText = pageText.substring(lineIndex, lineIndex + 200);
          const followingMatch = followingText.match(/([A-Za-z\s\.]+,\s*[A-Z]{2}(?:\s*\d{5})?)/);
          if (followingMatch) {
            result.origin = followingMatch[1].trim();
            console.log('Found origin (context match):', result.origin);
            break;
          }
        }
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error extracting data from PDF:', error);
    return {
      bolNumber: null,
      shipmentNumber: null,
      deliveryDate: null,
      origin: null,
      destination: null
    };
  }
};

// Helper function to parse date from various formats
const parseDate = (dateStr: string): Date | null => {
  try {
    // Handle different date formats (MM/DD/YYYY, MM-DD-YYYY, etc.)
    const formats = [
      /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/, // MM/DD/YYYY or MM-DD-YYYY or MM.DD.YYYY
      /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})/, // MM/DD/YY or MM-DD-YY or MM.DD.YY
    ];
    
    for (const format of formats) {
      const match = dateStr.match(format);
      if (match) {
        const [_, part1, part2, part3] = match;
        
        // Assume MM/DD/YYYY format (common in US)
        let month = parseInt(part1);
        let day = parseInt(part2);
        let year = parseInt(part3);
        
        // Handle 2-digit year
        if (year < 100) {
          year += year < 50 ? 2000 : 1900;
        }
        
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Try direct parsing as a fallback
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

// Legacy function for backward compatibility
export const extractBolNumber = async (pdfBytes: Uint8Array): Promise<string | null> => {
  const extractedData = await extractPdfData(pdfBytes);
  return extractedData.bolNumber;
};

// Extract lane info (origin/destination) from email body
export const extractLaneInfo = (emailBody: string): { origin: string, destination: string } => {
  const result = {
    origin: '',
    destination: ''
  };
  
  // Extract origin and destination from email body using regex
  const originMatch = emailBody.match(/origin[:\s]*([^\n]+)/i);
  if (originMatch && originMatch[1]) {
    result.origin = originMatch[1].trim();
  }
  
  const destMatch = emailBody.match(/destination[:\s]*([^\n]+)/i);
  if (destMatch && destMatch[1]) {
    result.destination = destMatch[1].trim();
  }
  
  return result;
};

// Get email attachment
export const getEmailAttachment = async (emailId: string, attachmentId: string): Promise<Uint8Array> => {
  try {
    // This would normally call a Cloud Function to get the attachment
    // For now, we're returning mock data
    return new Uint8Array(10);
  } catch (error) {
    console.error('Error getting email attachment:', error);
    throw error;
  }
};

// Schedule email scanning
export const scheduleEmailScanning = async (userId: string, intervalHours: number): Promise<boolean> => {
  try {
    // Call Firebase Function to schedule email scanning
    const scheduleEmailScan = httpsCallable(functions, 'scheduleEmailScan');
    const result = await scheduleEmailScan({ 
      userId, 
      intervalHours 
    });
    
    return result.data as boolean;
  } catch (error) {
    console.error('Error scheduling email scanning:', error);
    throw error;
  }
};

// Process emails for receipts
export const processEmailsForReceipts = async (
  userId: string, 
  carrierId: string, 
  carrierName: string, 
  hoursBack: number = 24
): Promise<number> => {
  // Simulate processing emails
  console.log('Processing emails for user', userId, 'with carrier', carrierId, 'looking back', hoursBack, 'hours');
  
  // In a real implementation, this would retrieve emails based on the hours back parameter,
  // process them, and create receipts with the specified carrier information
  return 0; // Return number of receipts created
}; 